import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MobileVideoSplashProps {
    onClose: () => void;
}

export default function MobileVideoSplash({ onClose }: MobileVideoSplashProps) {
    const [isVisible, setIsVisible] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('common');

    // Cache buster to bypass broken Service Worker or browser cache
    const [videoUrl] = useState(() => `/splash.mp4?v=${Math.random().toString(36).substring(7)}`);

    useEffect(() => {
        const handleSplashEnded = () => {
            handleClose();
        };

        const configVideo = () => {
            const video = containerRef.current?.querySelector('video');
            if (video) {
                video.onended = handleSplashEnded;
                video.onerror = handleClose;

                // Force load before play
                video.load();

                video.play().catch(e => {
                    console.warn("[MobileVideoSplash] Play attempt failed:", e);
                });
            }
        };

        // Try immediately
        configVideo();

        // Fallback: If autoplay is blocked, any touch on the screen will trigger it
        const handleInteraction = () => {
            configVideo();
        };
        window.addEventListener('pointerdown', handleInteraction);

        // Safety timeout to close splash if it hangs
        const timeout = setTimeout(handleClose, 15000);

        return () => {
            window.removeEventListener('pointerdown', handleInteraction);
            clearTimeout(timeout);
        };
    }, [videoUrl]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 500);
    };

    const toggleMute = () => {
        const video = containerRef.current?.querySelector('video');
        if (video) {
            video.muted = !video.muted;
            setIsMuted(video.muted);
        }
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    ref={containerRef}
                    initial={false}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ WebkitTransform: 'translateZ(0)' }}
                    className="fixed inset-0 w-full h-[100dvh] z-[10000] bg-black flex items-center justify-center overflow-hidden"
                >
                    {/* Native Video Injection - Bypasses React attribute handling for Safari */}
                    <div
                        className="absolute inset-0 w-full h-full"
                        dangerouslySetInnerHTML={{
                            __html: `
                                <video
                                    autoplay
                                    muted
                                    playsinline
                                    webkit-playsinline
                                    preload="auto"
                                    style="width: 100%; height: 100%; object-fit: cover;"
                                    src="${videoUrl}"
                                ></video>
                            `
                        }}
                    />

                    {/* Subtle Overlay */}
                    <div className="absolute inset-0 bg-black/10 pointer-events-none" />

                    {/* Controls */}
                    <div className="absolute bottom-10 left-0 right-0 px-6 flex justify-between items-center z-20">
                        <div className="flex items-center gap-4">
                            <div className="text-white/40 text-[10px] font-bold uppercase tracking-widest">
                                {t('status.loading')}...
                            </div>

                            <button
                                onClick={toggleMute}
                                className="p-3 bg-white/10 backdrop-blur-md rounded-full text-white/80 border border-white/20 active:scale-90 transition-all"
                            >
                                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                            </button>
                        </div>

                        <button
                            onClick={handleClose}
                            className="flex items-center gap-2 px-6 py-3 bg-white text-black font-black rounded-full shadow-lg shadow-black/50 active:scale-95 transition uppercase tracking-widest text-sm"
                        >
                            {t('onboarding_tour.actions.skip')} <X size={20} />
                        </button>
                    </div>

                    {/* Static Branding */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-10 pointer-events-none">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                            className="text-4xl font-black text-white tracking-tighter drop-shadow-2xl"
                        >
                            PADEL<span className="text-yellow-400">PARTNER</span>
                        </motion.div>
                        <motion.p
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="text-white/60 text-[10px] font-bold uppercase tracking-[0.4em] mt-2 drop-shadow-lg"
                        >
                            Join the Game
                        </motion.p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
